/**
 * The role picker: `src/ui/select.tsx`, plus one row `Select` has no way to
 * carry -- a permanently-disabled explanation for the role it never lists in
 * `invite` mode. ADDRESS_READER is never offered as an invitation target
 * (§18.2), and this explains why instead of silently dropping a role that
 * exists in the product -- an omission here reads to a tenant administrator
 * as the product being broken, not as a deliberate choice.
 *
 * Renders the same options regardless of who is looking. The API is what
 * refuses a role change or a grant, never this component (§3) -- there is no
 * caller identity anywhere in these props to make a decision from.
 */
import type { ReactNode } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { INVITABLE_ROLES, type MembershipRow } from '../api/schemas';
import { tt, type TenantMessageKey } from '../i18n';

export type RolePickerPurpose = 'invite' | 'assign';

type Role = MembershipRow['role'];

/** `INVITABLE_ROLES` (schemas.ts) has the two invitable roles; ADDRESS_READER
 *  is the one role that exists only here, appended for `assign` mode. */
const ALL_ROLES: readonly Role[] = [...INVITABLE_ROLES, 'ADDRESS_READER'];

const ROLE_LABEL_KEY: Record<Role, TenantMessageKey> = {
  TENANT_ADMIN: 'role.TENANT_ADMIN',
  CAMPAIGN_ANALYST: 'role.CAMPAIGN_ANALYST',
  ADDRESS_READER: 'role.ADDRESS_READER',
};

/** The closed trigger's own display: a short title, not the sentence
 *  `ROLE_LABEL_KEY` gives each `SelectItem` in the open list -- the trigger
 *  has no width to spare for a full sentence, and Radix's `Select.Value`
 *  otherwise mirrors whichever item's text last matched the value. */
const ROLE_SHORT_LABEL_KEY: Record<Role, TenantMessageKey> = {
  TENANT_ADMIN: 'role.short.TENANT_ADMIN',
  CAMPAIGN_ANALYST: 'role.short.CAMPAIGN_ANALYST',
  ADDRESS_READER: 'role.short.ADDRESS_READER',
};

export function RolePicker(props: {
  /** `invite` offers TENANT_ADMIN and CAMPAIGN_ANALYST and renders the
   *  ADDRESS_READER row as a permanently non-selectable explanation.
   *  `assign` offers all three: inviting the address role is forbidden, not
   *  granting it. Refusing it in both places would make it ungrantable. */
  purpose: RolePickerPurpose;
  value: string | null;
  onChange: (role: string) => void;
  /** Forwarded to the trigger button. The role labels are full sentences
   *  (`role.CAMPAIGN_ANALYST`), so a caller cramped for width passes a
   *  shrinkable size here rather than the default `w-fit`. */
  className?: string;
}): ReactNode {
  const { purpose, value, onChange, className } = props;
  const offered: readonly Role[] = purpose === 'invite' ? INVITABLE_ROLES : ALL_ROLES;

  return (
    <Select value={value ?? undefined} onValueChange={onChange}>
      <SelectTrigger aria-label={tt('invitations.create.roleLabel')} className={className}>
        <SelectValue placeholder={tt('invitations.create.roleLabel')}>
          {value === null ? undefined : tt(ROLE_SHORT_LABEL_KEY[value as Role])}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {offered.map((role) => (
          <SelectItem key={role} value={role}>
            {tt(ROLE_LABEL_KEY[role])}
          </SelectItem>
        ))}
        {purpose === 'invite' && (
          // Not a `SelectItem`: it must never carry role="option" or an
          // accessible name a screen reader announces as a choice -- it is
          // prose explaining an absence, and the other half of the rule (the
          // role IS choosable in `assign` mode) depends on the two never
          // being confused for one another.
          <div
            data-testid="role-address-note"
            className="border-t px-2 py-2 text-xs text-muted-foreground"
          >
            {tt('role.address.not_invitable')}
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
